import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, TYPE_OPTIONS } from "../lib/api";
import { Pencil, Trash2, Plus, ImagePlus, X, Mail, MailOpen, ExternalLink, Play, Pause, RefreshCw, AlertCircle, Check as CheckIcon, FileDown, Tag, Mic } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const emptyForm = { title: "", content: "", type: "pensamiento", tags: "", image: null, epigraph: "", audio_url: "" };
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function TextsSection() {
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/texts", { params: { limit: 500 } });
      setTexts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const openEdit = (t) => {
    setForm({
      title: t.title,
      content: t.content,
      type: t.type,
      tags: (t.tags || []).join(", "),
      image: t.image || null,
      epigraph: t.epigraph || "",
      audio_url: t.audio_url || "",
    });
    setEditing(t);
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const onImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("La imagen supera los 2 MB. Usa una más ligera.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, image: dataUrl }));
    } catch {
      toast.error("No se pudo leer la imagen.");
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content,
        type: form.type,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        image: form.image,
        epigraph: form.epigraph.trim(),
        audio_url: form.audio_url.trim(),
      };
      if (editing === "new") {
        await api.post("/texts", payload);
        toast.success("Texto publicado.");
      } else {
        await api.put(`/texts/${editing.id}`, payload);
        toast.success("Texto actualizado.");
      }
      close();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/texts/${toDelete.id}`);
      toast.success("Texto eliminado.");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportPoemas = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export/poemas-recital.xlsx", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.setAttribute("download", `poemas-recital-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel de poemas descargado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const [applyingTags, setApplyingTags] = useState(false);
  const applyRecitalTags = async () => {
    if (!window.confirm("¿Añadir el tag «recital» a los 95 poemas seleccionados? Es idempotente: puedes repetirlo sin riesgo.")) return;
    setApplyingTags(true);
    try {
      const { data } = await api.post("/admin/recital/apply-tags");
      toast.success(
        `Recital aplicado. Encontrados en BD: ${data.found_in_db}/${data.requested}. Total con tag «recital»: ${data.total_with_recital_tag}.`
      );
      if (data.missing?.length) {
        toast.warning(`${data.missing.length} ID(s) no existen en la base de datos.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplyingTags(false);
    }
  };

  const [cleaning, setCleaning] = useState(false);
  const cleanupStubs = async () => {
    if (!window.confirm("¿Eliminar los 24 stubs duplicados/vacíos? Esta acción borra registros de forma permanente.")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/recital/cleanup-stubs");
      toast.success(
        `Limpieza completada. Borrados: ${data.deleted}. Poemas restantes: ${data.remaining_poemas}.`
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCleaning(false);
    }
  };

  const [populatingVoice, setPopulatingVoice] = useState(false);
  const populateVoiceUrls = async () => {
    setPopulatingVoice(true);
    try {
      const { data } = await api.post("/admin/recital/populate-voice-urls");
      toast.success(
        `Voz propia poblada. ${data.total_with_audios_propia} poemas con audios.propia (de ${data.mp3_files} MP3).`
      );
      if (data.missing_in_db?.length) {
        toast.warning(`${data.missing_in_db.length} MP3 sin poema asociado en la base de datos.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPopulatingVoice(false);
    }
  };

  const [populatingJuanjo, setPopulatingJuanjo] = useState(false);
  const populateJuanjoUrls = async () => {
    setPopulatingJuanjo(true);
    try {
      const { data } = await api.post("/admin/recital/populate-juanjo-urls");
      toast.success(
        `Voz Juanjo poblada. ${data.total_with_audios_juanjo} poemas con audios.juanjo (de ${data.mp3_files} MP3).`
      );
      if (data.missing_in_db?.length) {
        toast.warning(`${data.missing_in_db.length} MP3 sin poema asociado en la base de datos.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPopulatingJuanjo(false);
    }
  };

  return (
    <>
      <div
        className="flex items-end justify-between pb-6 border-b"
        style={{ borderColor: "var(--rincon-border)" }}
      >
        <h2
          className="font-display text-3xl tracking-tight"
          style={{ color: "var(--rincon-text)" }}
        >
          Textos del cuaderno
        </h2>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button
            onClick={applyRecitalTags}
            disabled={applyingTags}
            data-testid="admin-apply-recital-button"
            className="inline-flex items-center gap-2 px-4 py-3 font-display text-base transition-colors border disabled:opacity-50"
            style={{ borderColor: "var(--rincon-primary)", color: "var(--rincon-primary)" }}
          >
            <Tag className="w-4 h-4" /> {applyingTags ? "Aplicando…" : "Aplicar recital (95)"}
          </button>
          <button
            onClick={cleanupStubs}
            disabled={cleaning}
            data-testid="admin-cleanup-stubs-button"
            className="inline-flex items-center gap-2 px-4 py-3 font-display text-base transition-colors border disabled:opacity-50"
            style={{ borderColor: "#9a3b3b", color: "#9a3b3b" }}
          >
            <Trash2 className="w-4 h-4" /> {cleaning ? "Limpiando…" : "Limpiar stubs (24)"}
          </button>
          <button
            onClick={populateVoiceUrls}
            disabled={populatingVoice}
            data-testid="admin-populate-voice-button"
            className="inline-flex items-center gap-2 px-4 py-3 font-display text-base transition-colors border disabled:opacity-50"
            style={{ borderColor: "var(--rincon-primary)", color: "var(--rincon-primary)" }}
          >
            <Mic className="w-4 h-4" /> {populatingVoice ? "Poblando…" : "Poblar URLs voz propia"}
          </button>
          <button
            onClick={populateJuanjoUrls}
            disabled={populatingJuanjo}
            data-testid="admin-populate-juanjo-button"
            className="inline-flex items-center gap-2 px-4 py-3 font-display text-base transition-colors border disabled:opacity-50"
            style={{ borderColor: "var(--rincon-primary)", color: "var(--rincon-primary)" }}
          >
            <Mic className="w-4 h-4" /> {populatingJuanjo ? "Poblando…" : "Poblar URLs voz Juanjo"}
          </button>
          <button
            onClick={exportPoemas}
            disabled={exporting}
            data-testid="admin-export-poemas-button"
            className="inline-flex items-center gap-2 px-4 py-3 font-display text-base transition-colors border disabled:opacity-50"
            style={{ borderColor: "var(--rincon-primary)", color: "var(--rincon-primary)" }}
          >
            <FileDown className="w-4 h-4" /> {exporting ? "Generando…" : "Excel poemas"}
          </button>
          <button
            onClick={openNew}
            data-testid="admin-new-text-button"
            className="inline-flex items-center gap-2 px-5 py-3 font-display text-lg transition-colors"
            style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
          >
            <Plus className="w-4 h-4" /> Nuevo texto
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="py-24 text-center font-display italic text-2xl"
          style={{ color: "var(--rincon-text-muted)" }}
        >
          Cargando...
        </div>
      ) : texts.length === 0 ? (
        <div
          className="py-24 text-center font-display italic text-2xl"
          style={{ color: "var(--rincon-text-muted)" }}
          data-testid="admin-empty"
        >
          Aún no has escrito nada. Pulsa «Nuevo texto» para empezar.
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full font-body" data-testid="admin-texts-table">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-[0.2em] border-b"
                style={{
                  color: "var(--rincon-text-muted)",
                  borderColor: "var(--rincon-border)",
                }}
              >
                <th className="py-4 pr-4 font-semibold">Título</th>
                <th className="py-4 pr-4 font-semibold">Categoría</th>
                <th className="py-4 pr-4 font-semibold">Imagen</th>
                <th className="py-4 pr-4 font-semibold">Fecha</th>
                <th className="py-4 pr-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {texts.map((t) => (
                <tr
                  key={t.id}
                  className="border-b"
                  style={{ borderColor: "var(--rincon-border)" }}
                  data-testid={`admin-row-${t.id}`}
                >
                  <td className="py-5 pr-4">
                    <div
                      className="font-display text-xl"
                      style={{ color: "var(--rincon-text)" }}
                    >
                      {t.title}
                    </div>
                  </td>
                  <td
                    className="py-5 pr-4 text-sm italic"
                    style={{ color: "var(--rincon-secondary)" }}
                  >
                    {t.main_category} · {t.subcategory}
                  </td>
                  <td className="py-5 pr-4">
                    {t.image ? (
                      <img
                        src={t.image}
                        alt=""
                        className="w-12 h-12 object-cover"
                        style={{ filter: "sepia(0.1)" }}
                      />
                    ) : (
                      <span
                        className="text-xs italic"
                        style={{ color: "var(--rincon-text-muted)" }}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td
                    className="py-5 pr-4 text-sm"
                    style={{ color: "var(--rincon-text-muted)" }}
                  >
                    {formatDate(t.created_at)}
                  </td>
                  <td className="py-5 pr-0 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(t)}
                      data-testid={`admin-edit-${t.id}`}
                      className="inline-flex items-center gap-1 mr-5 text-sm ink-link"
                      style={{ color: "var(--rincon-text)" }}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button
                      onClick={() => setToDelete(t)}
                      data-testid={`admin-delete-${t.id}`}
                      className="inline-flex items-center gap-1 text-sm ink-link"
                      style={{ color: "var(--rincon-error)" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Borrar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ background: "var(--rincon-bg)", borderColor: "var(--rincon-border)" }}
          data-testid="admin-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="font-display text-3xl font-normal"
              style={{ color: "var(--rincon-text)" }}
            >
              {editing === "new" ? "Nuevo texto" : "Editar texto"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para crear o editar un texto del cuaderno.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5 mt-2">
            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Título
              </label>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="admin-form-title"
                className="w-full bg-transparent border-b py-2 font-display text-2xl outline-none"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
              />
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Epígrafe / cita (opcional, se muestra en cursiva bajo el título)
              </label>
              <input
                type="text"
                value={form.epigraph}
                onChange={(e) => setForm({ ...form, epigraph: e.target.value })}
                data-testid="admin-form-epigraph"
                placeholder="Una frase breve, un verso ajeno, una dedicatoria..."
                className="w-full bg-transparent border-b py-2 font-body italic text-lg outline-none"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text-muted)" }}
              />
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Tipo
              </label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger
                  className="font-body"
                  data-testid="admin-form-type"
                  style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label} ({o.main})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Imagen (opcional, máx. 2 MB)
              </label>
              {form.image ? (
                <div className="relative inline-block">
                  <img
                    src={form.image}
                    alt="Vista previa"
                    data-testid="admin-form-image-preview"
                    className="max-h-48 w-auto border"
                    style={{ borderColor: "var(--rincon-border)" }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image: null })}
                    data-testid="admin-form-image-remove"
                    className="absolute -top-2 -right-2 rounded-full p-1 shadow"
                    style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
                    aria-label="Quitar imagen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  className="inline-flex items-center gap-2 px-4 py-2 border cursor-pointer font-body text-sm"
                  style={{
                    borderColor: "var(--rincon-border)",
                    color: "var(--rincon-text)",
                  }}
                  data-testid="admin-form-image-label"
                >
                  <ImagePlus className="w-4 h-4" /> Subir imagen
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onImageChange}
                    data-testid="admin-form-image-input"
                  />
                </label>
              )}
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                URL de audio (opcional · MP3 para recital)
              </label>
              <input
                type="url"
                value={form.audio_url}
                onChange={(e) => setForm({ ...form, audio_url: e.target.value })}
                data-testid="admin-form-audio-url"
                placeholder="https://.../poema.mp3"
                className="w-full bg-transparent border-b py-2 font-body text-base outline-none"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
              />
              <p
                className="mt-1 text-xs font-body"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Si el texto tiene grabación, pega aquí el enlace directo al archivo MP3 (por ejemplo el del Poemario).
              </p>
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Contenido
              </label>
              <textarea
                required
                rows={10}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                data-testid="admin-form-content"
                className="w-full bg-transparent border p-4 font-body text-base leading-relaxed outline-none resize-y"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
              />
            </div>

            <div>
              <label
                className="text-xs uppercase tracking-[0.2em] block mb-2"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Etiquetas (separadas por comas)
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                data-testid="admin-form-tags"
                placeholder="memoria, invierno, silencio"
                className="w-full bg-transparent border-b py-2 font-body outline-none"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
              />
            </div>

            <DialogFooter className="pt-4">
              <button
                type="button"
                onClick={close}
                data-testid="admin-form-cancel"
                className="px-5 py-2.5 font-display text-lg"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-form-submit"
                className="px-6 py-2.5 font-display text-lg transition-colors disabled:opacity-60"
                style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
              >
                {saving ? "Guardando..." : editing === "new" ? "Publicar" : "Guardar"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={toDelete !== null} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent
          style={{ background: "var(--rincon-bg)", borderColor: "var(--rincon-border)" }}
          data-testid="admin-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="font-display text-2xl font-normal"
              style={{ color: "var(--rincon-text)" }}
            >
              ¿Borrar este texto?
            </AlertDialogTitle>
            <AlertDialogDescription
              className="font-body italic"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Esta acción es definitiva: «{toDelete?.title}» desaparecerá del cuaderno.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="admin-delete-confirm"
              style={{ background: "var(--rincon-error)", color: "var(--rincon-bg)" }}
            >
              Borrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AuthorSection() {
  const [profile, setProfile] = useState(null);
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [biography, setBiography] = useState("");
  const [photo, setPhoto] = useState(null);
  const [featuredId, setFeaturedId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/author"),
        api.get("/texts", { params: { limit: 500 } }),
      ]);
      setProfile(p.data);
      setTexts(t.data);
      setBiography(p.data.biography || "");
      setPhoto(p.data.photo || null);
      setFeaturedId(p.data.featured_text_id || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("La foto supera los 2 MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPhoto(dataUrl);
    } catch {
      toast.error("No se pudo leer la imagen.");
    }
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/author", {
        photo,
        biography,
        featured_text_id: featuredId || null,
      });
      toast.success("Perfil actualizado.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="py-24 text-center font-display italic text-xl"
        style={{ color: "var(--rincon-text-muted)" }}
      >
        Cargando...
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-author-section">
      <h2
        className="font-display text-3xl tracking-tight pb-6 border-b"
        style={{ color: "var(--rincon-text)", borderColor: "var(--rincon-border)" }}
      >
        Sobre el autor
      </h2>

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <label
            className="text-xs uppercase tracking-[0.2em] block mb-3"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            Foto
          </label>
          {photo ? (
            <div className="relative inline-block">
              <img
                src={photo}
                alt="Foto del autor"
                className="w-full aspect-[3/4] object-cover"
                style={{ filter: "sepia(0.08)" }}
                data-testid="admin-author-photo-preview"
              />
              <button
                type="button"
                onClick={() => setPhoto(null)}
                data-testid="admin-author-photo-remove"
                className="absolute -top-2 -right-2 rounded-full p-1 shadow"
                style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
                aria-label="Quitar foto"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <label
              className="flex items-center justify-center aspect-[3/4] border cursor-pointer"
              style={{
                borderColor: "var(--rincon-border)",
                background: "var(--rincon-surface)",
                color: "var(--rincon-text-muted)",
              }}
              data-testid="admin-author-photo-label"
            >
              <div className="text-center font-body italic">
                <ImagePlus className="w-8 h-8 mx-auto mb-2" />
                Subir foto
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhoto}
                data-testid="admin-author-photo-input"
              />
            </label>
          )}
        </div>

        <div className="md:col-span-8 space-y-6">
          <div>
            <label
              className="text-xs uppercase tracking-[0.2em] block mb-3"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Biografía
            </label>
            <textarea
              rows={10}
              value={biography}
              onChange={(e) => setBiography(e.target.value)}
              data-testid="admin-author-bio"
              placeholder="Escribe aquí tu biografía..."
              className="w-full bg-transparent border p-4 font-body text-base leading-relaxed outline-none resize-y"
              style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
            />
          </div>

          <div>
            <label
              className="text-xs uppercase tracking-[0.2em] block mb-3"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Texto destacado (se muestra en tu página)
            </label>
            <Select value={featuredId || "none"} onValueChange={(v) => setFeaturedId(v === "none" ? "" : v)}>
              <SelectTrigger
                className="font-body"
                data-testid="admin-author-featured"
                style={{ borderColor: "var(--rincon-border)", color: "var(--rincon-text)" }}
              >
                <SelectValue placeholder="Ninguno" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="none">Ninguno</SelectItem>
                {texts.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title} — {t.subcategory}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="admin-author-save"
              className="px-6 py-2.5 font-display text-lg disabled:opacity-60"
              style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessagesSection({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "unread" ? { unread: true } : {};
      const { data } = await api.get("/messages", { params });
      setMessages(data);
      if (onUnreadChange) {
        const c = await api.get("/messages/unread-count");
        onUnreadChange(c.data.count);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const toggleRead = async (m) => {
    try {
      await api.patch(`/messages/${m.id}/read`, null, { params: { read: !m.read } });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/messages/${toDelete.id}`);
      toast.success("Mensaje eliminado.");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-messages-section">
      <div
        className="flex items-end justify-between pb-6 border-b flex-wrap gap-4"
        style={{ borderColor: "var(--rincon-border)" }}
      >
        <h2
          className="font-display text-3xl tracking-tight"
          style={{ color: "var(--rincon-text)" }}
        >
          Buzón de lectores
        </h2>
        <div className="flex gap-6">
          <button
            onClick={() => setFilter("all")}
            data-testid="admin-messages-filter-all"
            className={`font-display text-lg ink-link ${filter === "all" ? "font-semibold" : ""}`}
            style={{ color: filter === "all" ? "var(--rincon-primary)" : "var(--rincon-text-muted)" }}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter("unread")}
            data-testid="admin-messages-filter-unread"
            className={`font-display text-lg ink-link ${filter === "unread" ? "font-semibold" : ""}`}
            style={{ color: filter === "unread" ? "var(--rincon-primary)" : "var(--rincon-text-muted)" }}
          >
            Sin leer
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="py-24 text-center font-display italic text-xl"
          style={{ color: "var(--rincon-text-muted)" }}
        >
          Cargando...
        </div>
      ) : messages.length === 0 ? (
        <div
          className="py-24 text-center font-display italic text-2xl"
          style={{ color: "var(--rincon-text-muted)" }}
          data-testid="admin-messages-empty"
        >
          {filter === "unread" ? "No hay mensajes sin leer." : "El buzón está vacío."}
        </div>
      ) : (
        <ul
          className="mt-8 divide-y"
          style={{ borderColor: "var(--rincon-border)" }}
        >
          {messages.map((m) => (
            <li
              key={m.id}
              className="py-8 grid md:grid-cols-12 gap-4"
              style={{ borderColor: "var(--rincon-border)" }}
              data-testid={`admin-message-${m.id}`}
            >
              <div className="md:col-span-3">
                <div
                  className="font-display text-xl"
                  style={{ color: "var(--rincon-text)" }}
                >
                  {m.name}
                  {!m.read ? (
                    <span
                      className="ml-2 inline-block w-2 h-2 rounded-full align-middle"
                      style={{ background: "var(--rincon-primary)" }}
                      title="Sin leer"
                    />
                  ) : null}
                </div>
                {m.email ? (
                  <a
                    href={`mailto:${m.email}`}
                    className="text-sm ink-link break-all"
                    style={{ color: "var(--rincon-secondary)" }}
                  >
                    {m.email}
                  </a>
                ) : (
                  <div
                    className="text-sm italic"
                    style={{ color: "var(--rincon-text-muted)" }}
                  >
                    sin correo
                  </div>
                )}
                <div
                  className="text-xs mt-2"
                  style={{ color: "var(--rincon-text-muted)" }}
                >
                  {formatDate(m.created_at)}
                </div>
              </div>
              <div className="md:col-span-7">
                <p
                  className="font-body whitespace-pre-wrap"
                  style={{ color: "var(--rincon-text)", fontSize: "1.05rem", lineHeight: "1.7" }}
                >
                  {m.message}
                </p>
                {m.text_id ? (
                  <a
                    href={`/texto/${m.text_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-xs italic ink-link"
                    style={{ color: "var(--rincon-secondary)" }}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Sobre: {m.text_title || "texto"}
                  </a>
                ) : (
                  <div
                    className="mt-3 text-xs italic"
                    style={{ color: "var(--rincon-text-muted)" }}
                  >
                    Mensaje general
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex md:flex-col md:items-end gap-3 md:gap-2">
                <button
                  onClick={() => toggleRead(m)}
                  data-testid={`admin-message-toggle-${m.id}`}
                  className="inline-flex items-center gap-1 text-sm ink-link"
                  style={{ color: "var(--rincon-text)" }}
                >
                  {m.read ? (
                    <>
                      <Mail className="w-3.5 h-3.5" /> Marcar no leído
                    </>
                  ) : (
                    <>
                      <MailOpen className="w-3.5 h-3.5" /> Marcar leído
                    </>
                  )}
                </button>
                <button
                  onClick={() => setToDelete(m)}
                  data-testid={`admin-message-delete-${m.id}`}
                  className="inline-flex items-center gap-1 text-sm ink-link"
                  style={{ color: "var(--rincon-error)" }}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Borrar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={toDelete !== null} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent
          style={{ background: "var(--rincon-bg)", borderColor: "var(--rincon-border)" }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="font-display text-2xl font-normal"
              style={{ color: "var(--rincon-text)" }}
            >
              ¿Borrar este mensaje?
            </AlertDialogTitle>
            <AlertDialogDescription
              className="font-body italic"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              El mensaje de «{toDelete?.name}» desaparecerá para siempre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="admin-message-delete-confirm"
              style={{ background: "var(--rincon-error)", color: "var(--rincon-bg)" }}
            >
              Borrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SocialSection() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/admin/social/status"),
        api.get("/admin/social/history", { params: { days: 30 } }),
      ]);
      setStatus(s.data);
      setHistory(h.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleSchedule = async () => {
    try {
      const next = !status?.enabled;
      const { data } = await api.post("/admin/social/schedule", null, { params: { enabled: next } });
      setStatus(data);
      toast.success(next ? "Programación reanudada." : "Programación en pausa.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post("/admin/social/trigger");
      const items = data.items?.length || 0;
      toast.success(`Publicación lanzada: ${items} texto(s) procesado(s).`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTriggering(false);
    }
  };

  const verifyBluesky = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/admin/social/bluesky/verify");
      if (data.mocked) {
        toast.info("Bluesky en modo simulación: faltan BLUESKY_HANDLE / BLUESKY_APP_PASSWORD.");
      } else if (data.ok) {
        toast.success(`Bluesky autenticado ✓ Handle: ${data.handle} · DID: ${data.did?.slice(0, 24)}…`);
      } else {
        toast.error(`Bluesky no autenticó: ${data.error}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifying(false);
    }
  };

  const [verifyingTg, setVerifyingTg] = useState(false);
  const verifyTelegram = async () => {
    setVerifyingTg(true);
    try {
      const { data } = await api.post("/admin/social/telegram/verify");
      if (data.mocked) {
        toast.info("Telegram en modo simulación: faltan TELEGRAM_BOT_TOKEN / TELEGRAM_CHAT_ID.");
      } else if (data.ok) {
        toast.success(`Telegram OK ✓ Bot: @${data.bot} · Canal: ${data.channel}`);
      } else {
        toast.error(`Telegram no verificó: ${data.error}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifyingTg(false);
    }
  };

  // Filtro dinámico: plataformas activas siempre; plataformas en pausa solo muestran
  // su historial ANTERIOR a la fecha de pausa (se ocultan intentos/fantasmas posteriores).
  const enabledPlatforms = new Set(status?.enabled_platforms || []);
  const pausedPlatforms = status?.paused_platforms || {};
  const visibleHistory = history.filter((h) => {
    if (enabledPlatforms.has(h.platform)) return true;
    const paused = pausedPlatforms[h.platform];
    if (paused && paused.since) {
      return new Date(h.posted_at) < new Date(paused.since);
    }
    return false;
  });

  // Group history by date
  const byDay = {};
  visibleHistory.forEach((h) => {
    const day = h.posted_at.slice(0, 10);
    (byDay[day] = byDay[day] || []).push(h);
  });
  const days = Object.keys(byDay).sort().reverse();
  const errors = visibleHistory.filter((h) => !h.success);

  return (
    <div data-testid="admin-social-section" className="space-y-10">
      <div
        className="flex items-end justify-between pb-6 border-b flex-wrap gap-4"
        style={{ borderColor: "var(--rincon-border)" }}
      >
        <div>
          <h2 className="font-display text-3xl tracking-tight" style={{ color: "var(--rincon-text)" }}>
            Redes sociales
          </h2>
          {status ? (
            <p className="mt-2 font-body italic text-sm" style={{ color: "var(--rincon-text-muted)" }}>
              {status.enabled ? "Activa" : "En pausa"} · {status.batch_size} textos al día a las {String(status.hour).padStart(2,"0")}:{String(status.minute).padStart(2,"0")} ({status.timezone})
              {status.next_run ? ` · Próxima: ${new Date(status.next_run).toLocaleString("es-ES")}` : ""}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={toggleSchedule}
            data-testid="admin-social-toggle"
            className="inline-flex items-center gap-2 px-4 py-2.5 font-display text-base border-2"
            style={{ borderColor: "var(--rincon-primary)", color: "var(--rincon-primary)" }}
          >
            {status?.enabled ? <><Pause className="w-4 h-4" /> Pausar</> : <><Play className="w-4 h-4" /> Reanudar</>}
          </button>
          <button
            onClick={triggerNow}
            disabled={triggering}
            data-testid="admin-social-trigger"
            className="inline-flex items-center gap-2 px-4 py-2.5 font-display text-base disabled:opacity-60"
            style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
          >
            <RefreshCw className={`w-4 h-4 ${triggering ? "animate-spin" : ""}`} />
            {triggering ? "Publicando..." : "Publicar ahora"}
          </button>
        </div>
      </div>

      {/* Estado por plataforma */}
      {status?.platforms ? (
        <div data-testid="admin-social-platforms" className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--rincon-text-muted)" }}>
            Plataformas:
          </span>
          {status.platforms.map((p) => (
            <span
              key={p.platform}
              data-testid={`platform-status-${p.platform}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-display border"
              style={{
                borderColor: p.configured ? "var(--type-relato)" : "var(--rincon-border)",
                color: p.configured ? "var(--type-relato)" : "var(--rincon-text-muted)",
              }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: p.configured ? "var(--type-relato)" : "var(--rincon-text-muted)" }}
              />
              {p.label} {p.configured ? "· activa" : "· simulación"}
              {p.paused ? (
                <span className="italic" style={{ color: "var(--type-pensamiento, #b45309)" }}>· {p.note || "en pausa"}</span>
              ) : !p.enabled ? (
                <span className="italic" style={{ color: "var(--rincon-text-muted)" }}>· en pausa</span>
              ) : null}
              {p.platform === "bluesky" ? (
                <button
                  onClick={verifyBluesky}
                  disabled={verifying}
                  data-testid="admin-bluesky-verify"
                  className="ml-1 underline decoration-dotted disabled:opacity-50"
                  style={{ color: "var(--rincon-primary)" }}
                >
                  {verifying ? "verificando…" : "verificar"}
                </button>
              ) : null}
              {p.platform === "telegram" ? (
                <button
                  onClick={verifyTelegram}
                  disabled={verifyingTg}
                  data-testid="admin-telegram-verify"
                  className="ml-1 underline decoration-dotted disabled:opacity-50"
                  style={{ color: "var(--rincon-primary)" }}
                >
                  {verifyingTg ? "verificando…" : "verificar"}
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="py-16 text-center font-display italic text-xl" style={{ color: "var(--rincon-text-muted)" }}>
          Cargando...
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div data-testid="admin-social-errors">
          <h3 className="font-display text-xl mb-3 flex items-center gap-2" style={{ color: "var(--rincon-error)" }}>
            <AlertCircle className="w-4 h-4" /> Errores recientes ({errors.length})
          </h3>
          <ul className="space-y-2 text-sm font-body" style={{ color: "var(--rincon-text)" }}>
            {errors.slice(0, 10).map((e) => (
              <li key={e.id} className="border-l-4 pl-3 py-1" style={{ borderColor: "var(--rincon-error)" }}>
                <strong>[{e.platform}]</strong> «{e.text_title}» — {e.error}
                <span className="ml-2 text-xs italic" style={{ color: "var(--rincon-text-muted)" }}>
                  {new Date(e.posted_at).toLocaleString("es-ES")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {days.length === 0 && !loading ? (
        <div className="py-12 text-center font-display italic text-xl" style={{ color: "var(--rincon-text-muted)" }} data-testid="admin-social-empty">
          Aún no hay publicaciones. Pulsa «Publicar ahora» para probar.
        </div>
      ) : null}

      {days.map((day) => (
        <div key={day} data-testid={`admin-social-day-${day}`}>
          <h3 className="font-display text-xl mb-4 pb-2 border-b" style={{ color: "var(--rincon-text)", borderColor: "var(--rincon-border)" }}>
            {new Date(day).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </h3>
          <ul className="space-y-3">
            {byDay[day].map((h) => (
              <li key={h.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 py-2 items-center text-sm font-body" style={{ color: "var(--rincon-text)" }}>
                <div className="md:col-span-2 uppercase tracking-wider text-xs font-semibold" style={{ color: "var(--rincon-secondary)" }}>
                  {h.platform}{h.mocked ? " · (sim)" : ""}
                </div>
                <div className="md:col-span-6 italic">«{h.text_title}»</div>
                <div className="md:col-span-3">
                  {h.success ? (
                    <span className="inline-flex items-center gap-1" style={{ color: h.mocked ? "var(--rincon-text-muted)" : "var(--type-relato)" }}>
                      <CheckIcon className="w-3 h-3" /> publicado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1" style={{ color: "var(--rincon-error)" }}>
                      <AlertCircle className="w-3 h-3" /> {h.error?.slice(0, 60)}
                    </span>
                  )}
                </div>
                <div className="md:col-span-1 text-right">
                  {h.post_url ? (
                    <a href={h.post_url} target="_blank" rel="noopener noreferrer" className="ink-link" style={{ color: "var(--rincon-primary)" }}>
                      Ver →
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function StatsSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats/views");
        setStats(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div
        className="py-24 text-center font-display italic text-2xl"
        style={{ color: "var(--rincon-text-muted)" }}
        data-testid="stats-loading"
      >
        Cargando estadísticas...
      </div>
    );
  }

  if (!stats) return null;

  const cards = [
    { label: "Lecturas totales", value: stats.total_views, testid: "stats-total" },
    { label: "Últimos 7 días", value: stats.views_last_7_days, testid: "stats-last7" },
    { label: "Textos leídos", value: stats.distinct_texts_viewed, testid: "stats-distinct" },
  ];

  return (
    <div data-testid="stats-section">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
        {cards.map((c) => (
          <div
            key={c.testid}
            className="border-2 p-6"
            style={{ borderColor: "var(--rincon-primary)", background: "var(--rincon-surface)" }}
          >
            <div
              className="font-display text-4xl font-bold"
              style={{ color: "var(--rincon-primary)" }}
              data-testid={c.testid}
            >
              {c.value}
            </div>
            <div
              className="text-[10px] uppercase tracking-[0.2em] mt-2 font-semibold"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              {c.label}
            </div>
          </div>
        ))}
      </div>

      <h3
        className="font-display text-2xl tracking-tight mb-6"
        style={{ color: "var(--rincon-text)" }}
      >
        Textos más leídos
      </h3>
      {stats.top.length === 0 ? (
        <div
          className="py-16 text-center font-display italic text-xl"
          style={{ color: "var(--rincon-text-muted)" }}
          data-testid="stats-empty"
        >
          Aún no hay lecturas registradas.
        </div>
      ) : (
        <table className="w-full font-body" data-testid="stats-table">
          <thead>
            <tr
              className="text-left text-xs uppercase tracking-[0.2em] border-b"
              style={{ color: "var(--rincon-text-muted)", borderColor: "var(--rincon-border)" }}
            >
              <th className="py-3 w-10">#</th>
              <th className="py-3">Título</th>
              <th className="py-3">Categoría</th>
              <th className="py-3 text-right">Lecturas</th>
            </tr>
          </thead>
          <tbody>
            {stats.top.map((t, i) => (
              <tr
                key={t.id}
                className="border-b"
                style={{ borderColor: "var(--rincon-border)" }}
                data-testid={`stats-row-${t.id}`}
              >
                <td className="py-3" style={{ color: "var(--rincon-text-muted)" }}>{i + 1}</td>
                <td className="py-3">
                  <Link
                    to={`/texto/${t.id}`}
                    className="ink-link"
                    style={{ color: "var(--rincon-text)" }}
                  >
                    {t.title}
                  </Link>
                </td>
                <td className="py-3" style={{ color: "var(--rincon-text-muted)" }}>{t.subcategory}</td>
                <td
                  className="py-3 text-right font-display text-lg font-bold"
                  style={{ color: "var(--rincon-primary)" }}
                >
                  {t.views}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


export default function Admin() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        setUnread(data.count);
      } catch (e) {
        console.warn("No se pudo leer contador de mensajes:", e);
      }
    })();
  }, []);

  return (
    <div
      className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-32"
      data-testid="admin-page"
    >
      <div
        className="text-xs uppercase tracking-[0.35em] mb-4"
        style={{ color: "var(--rincon-secondary)" }}
      >
        Panel privado
      </div>
      <h1
        className="font-display text-5xl md:text-6xl tracking-tight mb-10"
        style={{ color: "var(--rincon-text)" }}
      >
        El taller
      </h1>

      <Tabs defaultValue="texts" className="w-full">
        <TabsList
          className="mb-10 bg-transparent gap-2 p-0 h-auto border-b rounded-none w-full justify-start"
          style={{ borderColor: "var(--rincon-border)" }}
        >
          <TabsTrigger
            value="texts"
            data-testid="admin-tab-texts"
            className="font-display text-lg rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[var(--rincon-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--rincon-primary)] data-[state=active]:shadow-none"
          >
            Textos
          </TabsTrigger>
          <TabsTrigger
            value="author"
            data-testid="admin-tab-author"
            className="font-display text-lg rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[var(--rincon-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--rincon-primary)] data-[state=active]:shadow-none"
          >
            Sobre el autor
          </TabsTrigger>
          <TabsTrigger
            value="messages"
            data-testid="admin-tab-messages"
            className="font-display text-lg rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[var(--rincon-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--rincon-primary)] data-[state=active]:shadow-none relative"
          >
            Buzón
            {unread > 0 ? (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs rounded-full"
                style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
                data-testid="admin-tab-messages-badge"
              >
                {unread}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger
            value="social"
            data-testid="admin-tab-social"
            className="font-display text-lg rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[var(--rincon-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--rincon-primary)] data-[state=active]:shadow-none"
          >
            Redes
          </TabsTrigger>
          <TabsTrigger
            value="stats"
            data-testid="admin-tab-stats"
            className="font-display text-lg rounded-none px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[var(--rincon-primary)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--rincon-primary)] data-[state=active]:shadow-none"
          >
            Estadísticas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="texts">
          <TextsSection />
        </TabsContent>
        <TabsContent value="author">
          <AuthorSection />
        </TabsContent>
        <TabsContent value="messages">
          <MessagesSection onUnreadChange={setUnread} />
        </TabsContent>
        <TabsContent value="social">
          <SocialSection />
        </TabsContent>
        <TabsContent value="stats">
          <StatsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
